import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import AutofillBanner from '../components/AutofillBanner';
import { useUserContext } from '../hooks/useUserContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Image as ImageIcon, 
  Loader2, 
  RefreshCw, 
  Download,
  Sparkles,
  Lock,
  Star,
  Heart,
  Target,
  Briefcase,
  Zap,
  Share2,
  Instagram,
  Linkedin,
  Square,
  X
} from 'lucide-react';

const imageIcons = {
  hero: Star,
  identity: Heart,
  business: Briefcase,
  impact: Target,
  lifestyle: Zap,
  word: Sparkles
};

const VisionBoardGeneratorContent = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [board, setBoard] = useState(null);
  const [visionSetupCompleted, setVisionSetupCompleted] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [generatingCollage, setGeneratingCollage] = useState(null);
  const [collagePreview, setCollagePreview] = useState(null);
  
  // Auto-fill context
  const { 
    hasData: hasContextData, 
    sources, 
    sourcesDisplay, 
    summaryMessage,
    getVision,
    getArtistic
  } = useUserContext();

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Check vision setup status
      const setupRes = await axios.get(`${API}/vision-setup/status`, { headers });
      setVisionSetupCompleted(setupRes.data.completed);
      
      // Check for existing board
      const boardRes = await axios.get(`${API}/vision-board/latest`, { headers });
      if (boardRes.data.board) {
        setBoard(boardRes.data.board);
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/vision-board/generate`, {}, { headers });
      setBoard(response.data);
      toast.success('Vision Board generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate vision board');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (imageKey) => {
    setRegenerating(imageKey);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/vision-board/regenerate/${imageKey}`, {}, { headers });
      
      // Update the image in the board
      const updatedImages = board.images.map(img => 
        img.key === imageKey ? { ...img, image: response.data.image } : img
      );
      setBoard({ ...board, images: updatedImages });
      toast.success('Image regenerated!');
    } catch (error) {
      toast.error('Failed to regenerate image');
    } finally {
      setRegenerating(null);
    }
  };

  const downloadImage = (imageBase64, title) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageBase64}`;
    link.download = `vision_board_${title.toLowerCase().replace(/\s+/g, '_')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateCollage = async (format) => {
    setGeneratingCollage(format);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/vision-board/share-collage`, { format }, { headers });
      setCollagePreview({
        format,
        dimensions: response.data.dimensions,
        image: response.data.collage_base64
      });
      toast.success(`${format.charAt(0).toUpperCase() + format.slice(1)} collage created!`);
    } catch (error) {
      toast.error('Failed to generate collage');
    } finally {
      setGeneratingCollage(null);
    }
  };

  const downloadCollage = () => {
    if (!collagePreview) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${collagePreview.image}`;
    link.download = `vision_board_${collagePreview.format}_${new Date().toISOString().split('T')[0]}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Collage downloaded!');
  };

  // Share Modal Component
  const ShareModal = () => (
    <AnimatePresence>
      {showShareModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => { setShowShareModal(false); setCollagePreview(null); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-[#0A0A0A] border border-[#1C1C1C] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-[#1C1C1C] flex items-center justify-between">
              <div>
                <h3 className="font-serif text-xl text-white">Share Your Vision Board</h3>
                <p className="text-[#A1A1AA] text-sm mt-1">Create a beautiful collage for social media</p>
              </div>
              <button
                onClick={() => { setShowShareModal(false); setCollagePreview(null); }}
                className="text-[#52525B] hover:text-white transition-colors p-2"
              >
                <X size={20} />
              </button>
            </div>

            {/* Format Selection */}
            <div className="p-6 border-b border-[#1C1C1C]">
              <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-4">CHOOSE FORMAT</p>
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => generateCollage('instagram')}
                  disabled={generatingCollage}
                  className={`p-4 border transition-all flex flex-col items-center gap-3 ${
                    collagePreview?.format === 'instagram'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#27272A] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <Instagram className="text-[#D4AF37]" size={24} />
                  <span className="text-white text-sm">Instagram</span>
                  <span className="text-[#52525B] text-xs">1080 × 1350</span>
                  {generatingCollage === 'instagram' && (
                    <Loader2 className="animate-spin text-[#D4AF37]" size={16} />
                  )}
                </button>

                <button
                  onClick={() => generateCollage('linkedin')}
                  disabled={generatingCollage}
                  className={`p-4 border transition-all flex flex-col items-center gap-3 ${
                    collagePreview?.format === 'linkedin'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#27272A] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <Linkedin className="text-[#D4AF37]" size={24} />
                  <span className="text-white text-sm">LinkedIn</span>
                  <span className="text-[#52525B] text-xs">1200 × 627</span>
                  {generatingCollage === 'linkedin' && (
                    <Loader2 className="animate-spin text-[#D4AF37]" size={16} />
                  )}
                </button>

                <button
                  onClick={() => generateCollage('square')}
                  disabled={generatingCollage}
                  className={`p-4 border transition-all flex flex-col items-center gap-3 ${
                    collagePreview?.format === 'square'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#27272A] hover:border-[#D4AF37]/50'
                  }`}
                >
                  <Square className="text-[#D4AF37]" size={24} />
                  <span className="text-white text-sm">Square</span>
                  <span className="text-[#52525B] text-xs">1080 × 1080</span>
                  {generatingCollage === 'square' && (
                    <Loader2 className="animate-spin text-[#D4AF37]" size={16} />
                  )}
                </button>
              </div>
            </div>

            {/* Preview */}
            {collagePreview && (
              <div className="p-6">
                <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-4">PREVIEW</p>
                <div className="bg-[#1C1C1C] p-4 flex justify-center">
                  <img
                    src={`data:image/png;base64,${collagePreview.image}`}
                    alt="Vision Board Collage"
                    className="max-w-full max-h-[400px] object-contain"
                  />
                </div>
                
                <div className="flex gap-4 mt-6">
                  <Button
                    onClick={downloadCollage}
                    className="btn-gold flex-1 py-4 flex items-center justify-center gap-2"
                  >
                    <Download size={18} />
                    Download {collagePreview.dimensions}
                  </Button>
                </div>

                <p className="text-[#52525B] text-xs text-center mt-4">
                  Download and share on your favorite platform!
                </p>
              </div>
            )}

            {!collagePreview && (
              <div className="p-12 text-center">
                <Share2 className="text-[#27272A] mx-auto mb-4" size={48} />
                <p className="text-[#52525B]">Select a format above to generate your shareable collage</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (loading) {
    return (
      <DashboardLayout title="Vision Board Generator">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#D4AF37]" size={48} />
        </div>
      </DashboardLayout>
    );
  }

  // Locked state - Vision Setup not completed
  if (!visionSetupCompleted) {
    return (
      <DashboardLayout title="Vision Board Generator">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-[#1C1C1C] flex items-center justify-center">
              <Lock className="text-[#52525B]" size={32} />
            </div>
            
            <h2 className="font-serif text-2xl text-white mb-4">
              Complete Vision Setup First
            </h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Your Vision Board is generated based on your vision, identity, goals, and brand direction. 
              Complete the Vision Setup to unlock this feature.
            </p>

            <Button
              onClick={() => window.location.href = '/vision-setup'}
              className="btn-gold px-8 py-4"
            >
              Start Vision Setup
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Donner vie à ta marque">
      <div className="max-w-6xl mx-auto">
        {/* Auto-fill Banner */}
        {hasContextData && !board && (
          <AutofillBanner
            hasData={hasContextData}
            sources={sources}
            sourcesDisplay={sourcesDisplay}
            message={summaryMessage}
            showDetails={true}
          />
        )}
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <ImageIcon className="text-[#D4AF37] mb-4" size={32} />
              <h2 className="font-serif text-2xl text-white mb-2">
                Your Vision Board
              </h2>
              <p className="text-[#A1A1AA]">
                A visual representation of your vision, identity, and goals
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="btn-gold px-8 py-4"
                data-testid="generate-board"
              >
                {generating ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Generating Images...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={18} />
                    {board ? 'Regenerate All' : 'Generate Vision Board'}
                  </>
                )}
              </Button>
              
              {board && board.images?.length > 0 && (
                <Button
                  onClick={() => setShowShareModal(true)}
                  className="bg-[#1C1C1C] text-white border border-[#D4AF37]/50 hover:bg-[#27272A] px-6 py-4"
                  data-testid="share-board"
                >
                  <Share2 className="mr-2" size={18} />
                  Share
                </Button>
              )}
            </div>
          </div>
        </motion.div>
        
        {/* Share Modal */}
        <ShareModal />

        {board ? (
          <>
            {/* Vision Statement Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-premium p-6 mb-8 bg-gradient-to-r from-[#D4AF37]/10 to-transparent"
            >
              <div className="text-center max-w-3xl mx-auto">
                <Star className="text-[#D4AF37] mx-auto mb-3" size={24} />
                <p className="text-white font-serif text-lg italic leading-relaxed mb-4">
                  "{board.vision_statement}"
                </p>
                {board.tagline && (
                  <p className="text-[#E5C585] text-sm">{board.tagline}</p>
                )}
              </div>
            </motion.div>

            {/* Vision Board Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {board.images?.map((img, index) => {
                const Icon = imageIcons[img.key] || ImageIcon;
                
                return (
                  <motion.div
                    key={img.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + index * 0.05 }}
                    className="card-premium overflow-hidden group"
                  >
                    {/* Image */}
                    <div className="relative aspect-square">
                      <img
                        src={`data:image/png;base64,${img.image}`}
                        alt={img.title}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                          <button
                            onClick={() => handleRegenerate(img.key)}
                            disabled={regenerating === img.key}
                            className="flex-1 bg-[#1C1C1C]/90 text-white py-2 px-3 text-sm flex items-center justify-center gap-2 hover:bg-[#27272A] transition-colors"
                          >
                            {regenerating === img.key ? (
                              <Loader2 className="animate-spin" size={14} />
                            ) : (
                              <RefreshCw size={14} />
                            )}
                            Regenerate
                          </button>
                          <button
                            onClick={() => downloadImage(img.image, img.title)}
                            className="bg-[#D4AF37] text-black py-2 px-3 hover:bg-[#E5C585] transition-colors"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <div className="p-4 flex items-center gap-3">
                      <Icon className="text-[#D4AF37]" size={18} />
                      <span className="text-white font-serif">{img.title}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Core Values & Brand Essence */}
            <div className="grid md:grid-cols-2 gap-6">
              {board.core_values && board.core_values.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="card-premium p-6"
                >
                  <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-4">CORE VALUES</p>
                  <div className="flex flex-wrap gap-2">
                    {board.core_values.map((value, i) => (
                      <span key={i} className="px-4 py-2 bg-[#1C1C1C] text-[#D4AF37] text-sm border border-[#D4AF37]/20">
                        {value}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {(board.brand_essence || board.word_of_year) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 }}
                  className="card-premium p-6 text-center flex flex-col justify-center"
                >
                  <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-3">
                    {board.word_of_year ? 'WORD OF THE YEAR' : 'BRAND ESSENCE'}
                  </p>
                  <p className="text-[#D4AF37] font-serif text-4xl">
                    {board.word_of_year || board.brand_essence}
                  </p>
                </motion.div>
              )}
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <ImageIcon className="text-[#27272A] mx-auto mb-6" size={64} />
            <h3 className="font-serif text-xl text-white mb-3">No Vision Board Yet</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto mb-8">
              Click "Generate Vision Board" to create a personalized visual board based on your vision, identity, and goals.
            </p>
            <p className="text-[#52525B] text-sm">
              This will generate 6 AI-created images representing different aspects of your brand vision.
            </p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const VisionBoardGenerator = () => {
  return (
    <PrerequisiteGate 
      toolId="vision-board-generator" 
      toolTitle="Donner vie à ta marque"
    >
      <VisionBoardGeneratorContent />
    </PrerequisiteGate>
  );
};

export default VisionBoardGenerator;
